import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Reveal } from "@/components/Reveal";
import { ConsequentialGame } from "@/components/ConsequentialGame";
import { getArtworks, getSeries } from "@/lib/api";

const SERIES = [
  { value: "All", label: "All" },
  { value: "The Metamorphosis of Self: The Six Stages of Grief", label: "Metamorphosis of Self" },
  { value: "The Consequential Game", label: "Consequential Game" },
  { value: "The Death, Rebirth and the In-Between", label: "Death, Rebirth & In-Between" },
  { value: "Installations", label: "Installations" },
];

export default function GalleryPage() {
  useSEO(
    "Artwork Gallery · The House of Ego · Seyoun Taylor",
    "Original surrealist artworks by Seyoun Taylor: The Metamorphosis of Self: The Six Stages of Grief, The Consequential Game, and The Death, Rebirth and the In-Between. Prints, custom sizes, and commissions by inquiry."
  );
  const [artworks, setArtworks] = useState([]);
  const [series, setSeries] = useState("All");
  const [statements, setStatements] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSeries()
      .then((list) => setStatements(Object.fromEntries(list.map((s) => [s.series, s.statement]))))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getArtworks(series)
      .then(setArtworks)
      .catch(() => setArtworks([]))
      .finally(() => setLoading(false));
  }, [series]);

  return (
    <main id="main-content" data-testid="gallery-page">
      <section className="mx-auto max-w-7xl px-6 md:px-10 pt-40 md:pt-52 pb-14">
        <Reveal>
          <p className="eyebrow">The Artwork</p>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl tracking-tight text-ink leading-[1.05]">
            The Gallery
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-neutral-600">
            Surrealist works that begin as personal discovery and become an invitation to your
            journey. Resonate with your own story inside them. Originals, prints, custom sizes, and
            commissions by inquiry.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="mt-10 flex flex-wrap gap-2" role="group" aria-label="Filter by series">
            {SERIES.map((s) => (
              <button
                key={s.value}
                type="button"
                data-testid={`filter-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setSeries(s.value)}
                aria-pressed={series === s.value}
                className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
                  series === s.value
                    ? "bg-royal text-white border-royal"
                    : "border-neutral-300 text-neutral-600 hover:border-royal hover:text-royal"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </Reveal>
      </section>

      {series !== "All" && statements[series] && (
        <section className="mx-auto max-w-7xl px-6 md:px-10 pb-14" data-testid="series-statement">
          <Reveal>
            <div className="max-w-3xl border-l-2 border-royal pl-6 space-y-4">
              {statements[series].split("\n\n").map((p, i) => (
                <p key={i} className="text-lg leading-relaxed text-neutral-600">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>
        </section>
      )}

      {series === "The Consequential Game" && (
        <section className="mx-auto max-w-7xl px-6 md:px-10 pb-16">
          <Reveal>
            <ConsequentialGame />
          </Reveal>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-6 md:px-10 pb-24 md:pb-32">
        {loading ? (
          <p className="py-20 text-center text-neutral-400 text-sm" data-testid="gallery-loading">
            Hanging the works…
          </p>
        ) : artworks.length === 0 ? (
          <p className="py-20 text-center text-neutral-400 text-sm" data-testid="gallery-empty">
            No works in this series yet.
          </p>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14"
            data-testid="gallery-grid"
          >
            {artworks.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.08}>
                <Link
                  to={`/gallery/${a.slug}`}
                  data-testid={`artwork-card-${a.slug}`}
                  className="group block"
                >
                  <div className="overflow-hidden bg-stone2 aspect-[4/5]">
                    <img
                      src={a.image_url}
                      alt={a.series ? `${a.title}, ${a.series} series, artwork by Seyoun Taylor` : `${a.title}, artwork by Seyoun Taylor`}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>
                  <div className="mt-5 flex items-baseline justify-between gap-4">
                    <div>
                      <h2 className="font-serif text-2xl text-ink group-hover:text-royal transition-colors">
                        {a.title}
                      </h2>
                      <p className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-500">
                        {[a.series, a.size].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                    <p className="text-xs text-neutral-400 shrink-0">{a.price}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
